import React, { useRef, useState, useEffect, useCallback } from "react";

interface AutoFitPunchlineProps {
  punchline: string;
  className?: string;
}

let measureCanvas: HTMLCanvasElement | null = null;
let measureCtx: CanvasRenderingContext2D | null = null;

function measureTextWidth(text: string, font: string): number {
  if (typeof document === "undefined") return text.length * 55;
  if (!measureCanvas) {
    measureCanvas = document.createElement("canvas");
    measureCtx = measureCanvas.getContext("2d");
  }
  if (!measureCtx) return text.length * 55;
  measureCtx.font = font;
  return measureCtx.measureText(text).width;
}

export function AutoFitPunchline({ punchline, className = "" }: AutoFitPunchlineProps) {
  const containerRef = useRef<HTMLHeadingElement>(null);
  const [fontSize, setFontSize] = useState<number | null>(null);

  const lines = (punchline || "")
    .replace(/\\n/g, "\n")
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

  const calculateFontSize = useCallback((width: number, linesList: string[]) => {
    if (width <= 0 || linesList.length === 0) return null;

    // Use Montserrat font string matching styles/globals.css
    const testFont = "800 100px Montserrat, sans-serif";
    const maxWidthAt100px = Math.max(
      ...linesList.map((line) => measureTextWidth(line, testFont))
    );

    if (maxWidthAt100px <= 0) return null;

    // Direct proportional scaling with a 5% safety margin
    const rawSize = (width / maxWidthAt100px) * 100 * 0.95;

    // Responsive boundaries: maximum 60px on desktop (text-6xl), minimum 18px on narrow mobile
    const maxAllowed = width > 768 ? 60 : width > 480 ? 44 : 34;
    const minAllowed = 18;

    return Math.round(Math.min(maxAllowed, Math.max(minAllowed, rawSize)) * 10) / 10;
  }, []);

  const updateSize = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const width = el.clientWidth;
    const nextSize = calculateFontSize(width, lines);
    if (nextSize && nextSize !== fontSize) {
      setFontSize(nextSize);
    }
  }, [calculateFontSize, fontSize, lines]);

  useEffect(() => {
    updateSize();

    const el = containerRef.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      if (!entries || entries.length === 0) return;
      const contentWidth = entries[0]?.contentRect?.width;
      if (contentWidth && contentWidth > 0) {
        const nextSize = calculateFontSize(contentWidth, lines);
        if (nextSize) {
          setFontSize(nextSize);
        }
      }
    });

    observer.observe(el);

    if (typeof document !== "undefined" && document.fonts) {
      document.fonts.ready.then(() => updateSize());
    }

    return () => observer.disconnect();
  }, [calculateFontSize, lines, updateSize]);

  const fallbackClass = fontSize ? "" : "text-4xl md:text-5xl lg:text-6xl";

  return (
    <h2
      ref={containerRef}
      className={`font-extrabold tracking-tight text-brand-primary leading-tight ${fallbackClass} ${className}`.trim()}
      style={fontSize ? { fontSize: `${fontSize}px`, lineHeight: 1.15 } : undefined}
    >
      {lines.map((line, idx) => (
        <span key={idx} className="block whitespace-nowrap overflow-hidden text-ellipsis">
          {line}
        </span>
      ))}
    </h2>
  );
}
